import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { Button } from "./components/ui/button";
import { Heart, Menu, X, Globe, Lock } from "lucide-react";
import { LanguageProvider, useLanguage } from "./context/LanguageContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import "@/App.css";

import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import EventsPage from "./pages/EventsPage";
import GalleryPage from "./pages/GalleryPage";
import ContactPage from "./pages/ContactPage";
import PrivateCoachingPage from "./pages/PrivateCoachingPage";
import TournamentBracketsPage from "./pages/TournamentBracketsPage";
import EventDetailPage from "./pages/EventDetailPage";
import LoginPage from "./pages/admin/LoginPage";
import AdminLayout from "./pages/admin/AdminLayout";
import DashboardPage from "./pages/admin/DashboardPage";
import AthletesPage from "./pages/admin/AthletesPage";
import AttendancePage from "./pages/admin/AttendancePage";
import AcademicsPage from "./pages/admin/AcademicsPage";
import GearPage from "./pages/admin/GearPage";
import TeamsPage from "./pages/admin/TeamsPage";
import TournamentsPage from "./pages/admin/TournamentsPage";
import AnalyticsPage from "./pages/admin/AnalyticsPage";
import AdminEventsPage from "./pages/admin/AdminEventsPage";
import ContentEditorPage from "./pages/admin/ContentEditorPage";
import GalleryManagerPage from "./pages/admin/GalleryManagerPage";

const GOFUNDME_URL = "https://gofund.me/274183352";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a9038138-35cf-4d7a-8b3b-ccb6b5c034cc/artifacts/7e22fcgr_image.png";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { lang, toggle, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location]);

  const navLinks = [
    { label: t.nav.home, to: "/" },
    { label: t.nav.about, to: "/about" },
    { label: t.nav.coaching, to: "/private-coaching" },
    { label: t.nav.events, to: "/events" },
    { label: t.nav.brackets, to: "/brackets" },
    { label: t.nav.gallery, to: "/gallery" },
    { label: t.nav.contact, to: "/contact" }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 overflow-visible ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white/80 backdrop-blur-sm"
      }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="relative z-20 flex-shrink-0" data-testid="logo">
            <div className="relative" style={{ marginTop: '12px', marginBottom: '-24px' }}>
              <div className="w-28 h-28 rounded-full bg-white shadow-lg border-2 border-gray-100 overflow-hidden flex items-center justify-center hover:scale-105 transition-transform duration-300">
                <img src={LOGO_URL} alt="Curious 123 Logo" className="w-36 h-36 object-contain" style={{ transform: 'scale(1.9)', marginTop: '-4px' }} />
              </div>
            </div>
          </Link>
          <div className="hidden lg:flex items-center gap-5">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}
                className={`nav-link font-medium transition-colors text-sm ${isActive(link.to) ? "text-[#87CEEB]" : "text-black hover:text-gray-600"}`}>
                {link.label}
              </Link>
            ))}
            <button onClick={toggle} data-testid="language-toggle" aria-label="Toggle language"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 hover:border-[#87CEEB] transition-colors text-sm font-medium">
              <Globe className="w-4 h-4" />{lang === "en" ? "ES" : "EN"}
            </button>
            <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
              <Button className="donate-btn rounded-full px-6 py-2 font-semibold uppercase tracking-wider text-sm">
                <Heart className="w-4 h-4 mr-2" />{t.nav.donate}
              </Button>
            </a>
            <Link to="/admin/login" data-testid="coach-login-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 hover:border-[#87CEEB] hover:bg-[#87CEEB]/10 transition-colors text-sm font-medium text-gray-600 hover:text-black">
              <Lock className="w-3.5 h-3.5" />Coach
            </Link>
          </div>
          <div className="lg:hidden flex items-center gap-2 ml-auto">
            <button onClick={toggle} data-testid="language-toggle-mobile"
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-gray-300 text-sm font-medium">
              <Globe className="w-3.5 h-3.5" />{lang === "en" ? "ES" : "EN"}
            </button>
            <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="lg:hidden mobile-menu absolute top-20 left-0 right-0 py-4 px-4 shadow-lg bg-white">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link key={link.to} to={link.to}
                className={`font-medium py-3 px-4 rounded-lg transition-colors ${isActive(link.to) ? "bg-[#87CEEB]/20 text-[#87CEEB]" : "text-black hover:bg-gray-100"}`}>
                {link.label}
              </Link>
            ))}
            <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer" className="w-full mt-2">
              <Button className="donate-btn rounded-full w-full py-3 font-semibold uppercase tracking-wider">
                <Heart className="w-4 h-4 mr-2" />{t.nav.donateNow}
              </Button>
            </a>
            <Link to="/admin/login" data-testid="coach-login-btn-mobile"
              className="flex items-center justify-center gap-2 w-full mt-2 py-3 px-4 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 font-medium transition-colors">
              <Lock className="w-4 h-4" />Coach Login
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

const Footer = () => {
  const { t } = useLanguage();
  return (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <img src={LOGO_URL} alt="Curious 123 Logo" className="h-16 w-auto" />
            </div>
            <p className="text-[#87CEEB] font-semibold mb-2 font-['Kanit']">{t.footer.tagline}</p>
            <p className="text-gray-400 mb-4">{t.footer.desc}</p>
            <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer">
              <Button className="donate-btn rounded-full px-6 py-2 font-semibold">
                <Heart className="w-4 h-4 mr-2" />{t.nav.donateNow}
              </Button>
            </a>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4 font-['Kanit']">{t.footer.quickLinks}</h3>
            <ul className="space-y-2">
              {[
                { label: t.footer.home, to: "/" },
                { label: t.footer.aboutUs, to: "/about" },
                { label: t.footer.events, to: "/events" },
                { label: t.nav.brackets, to: "/brackets" },
                { label: t.footer.gallery, to: "/gallery" },
                { label: t.footer.privateCoaching, to: "/private-coaching" },
                { label: t.footer.contactLink, to: "/contact" }
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-[#87CEEB] transition-colors">{link.label}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-bold text-lg mb-4 font-['Kanit']">{t.footer.contact}</h3>
            <ul className="space-y-2 text-gray-400">
              <li><a href="mailto:curious123soccer@gmail.com" className="hover:text-[#87CEEB] transition-colors">curious123soccer@gmail.com</a></li>
              <li>San Juan del Sur, Nicaragua</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} Curious 123. {t.footer.rights}</p>
          <div className="flex gap-4 text-sm text-gray-500">
            <Link to="/about" className="hover:text-[#87CEEB] transition-colors">{t.footer.about}</Link>
            <Link to="/contact" className="hover:text-[#87CEEB] transition-colors">{t.footer.contact}</Link>
            <a href={GOFUNDME_URL} target="_blank" rel="noopener noreferrer" className="hover:text-[#87CEEB] transition-colors">{t.footer.donate}</a>
          </div>
        </div>
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-xs">Created by <a href="https://www.GregMiceli.com" target="_blank" rel="noopener noreferrer" className="text-[#87CEEB] hover:text-white transition-colors">www.GregMiceli.com</a></p>
        </div>
      </div>
    </footer>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#f1f5f9]">
      <div className="animate-spin w-8 h-8 border-4 border-[#87CEEB] border-t-transparent rounded-full" />
    </div>
  );
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
};

const AdminRoutes = () => (
  <ProtectedRoute>
    <AdminLayout>
      <Routes>
        <Route index element={<DashboardPage />} />
        <Route path="athletes" element={<AthletesPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="academics" element={<AcademicsPage />} />
        <Route path="gear" element={<GearPage />} />
        <Route path="teams" element={<TeamsPage />} />
        <Route path="tournaments" element={<TournamentsPage />} />
        <Route path="events" element={<AdminEventsPage />} />
        <Route path="content" element={<ContentEditorPage />} />
        <Route path="gallery" element={<GalleryManagerPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
      </Routes>
    </AdminLayout>
  </ProtectedRoute>
);

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith("/admin");
  const API = '';

  // Analytics: track page views on public pages
  useEffect(() => {
    if (isAdmin) return;
    const trackPageView = async () => {
      try {
        await fetch(`${API}/api/analytics/track`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event_type: "pageview", page: location.pathname, referrer: document.referrer || null }),
        });
      } catch (e) { /* silent */ }
    };
    trackPageView();
  }, [location.pathname, isAdmin, API]);

  // Analytics: track clicks on public pages
  useEffect(() => {
    if (isAdmin) return;
    const handleClick = (e) => {
      const target = e.target.closest("a, button");
      if (!target) return;
      const label = target.textContent?.trim().substring(0, 80) || target.getAttribute("aria-label") || "unknown";
      fetch(`${API}/api/analytics/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_type: "click", page: window.location.pathname, element: label }),
      }).catch(() => {});
    };
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [isAdmin, API]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      a[href*="emergent.sh"],
      div[class^="ve-"],
      div[class*=" ve-"],
      #__visual_edits_overlay__ {
        display: none !important;
        visibility: hidden !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  return (
    <>
      <Toaster position="top-right" richColors />
      {!isAdmin && <Navigation />}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/private-coaching" element={<PrivateCoachingPage />} />
          <Route path="/brackets" element={<TournamentBracketsPage />} />
          <Route path="/admin/login" element={<LoginPage />} />
          <Route path="/admin/*" element={<AdminRoutes />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <HelmetProvider>
          <BrowserRouter>
            <div className="App" data-testid="app-container">
              <AppContent />
            </div>
          </BrowserRouter>
        </HelmetProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
